/**
 * Remote Debug Middleware
 * Sends Redux actions and state changes to backend for logging
 */
import { logger } from './consoleEventLogger';

const remoteDebugEnabled = process.env.REACT_APP_REMOTE_DEBUG === '1';
const baseUrl = process.env.REACT_APP_BASE_URL;

// Generate session ID for tracking logs
const generateSessionId = () => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

const sessionId = generateSessionId();

// Export session ID for use by other loggers (like console event logger)
export { sessionId };
let actionCounter = 0;

const remoteDebugMiddleware = (store) => (next) => (action) => {
  // Always let the action through
  const result = next(action);

  // Send to backend if remote debug is enabled
  if (remoteDebugEnabled) {
    const state = store.getState();
    const timestamp = formatTimestamp();
    actionCounter++;

    const debugPayload = {
      session_id: sessionId,
      action_number: actionCounter,
      action_type: action.type,
      action_payload: objectToArray(sanitizePayload(action.payload)),
      state_snapshot: objectToArray(sanitizeState(state)),
      timestamp: timestamp,
      user_agent: navigator.userAgent,
      url: window.location.href,
    };

    // Send to backend asynchronously
    sendDebugLog(debugPayload);
  }

  return result;
};

/**
 * Remove sensitive data from payload
 */
const sanitizePayload = (payload) => {
  if (!payload) return null;
  if (typeof payload !== 'object') return payload;

  const sensitiveKeys = ['password', 'token', 'credentials', 'accessKey', 'secretKey'];
  const sanitized = { ...payload };

  Object.keys(sanitized).forEach((key) => {
    if (sensitiveKeys.some((sk) => key.toLowerCase().includes(sk.toLowerCase()))) {
      sanitized[key] = '***REDACTED***';
    }
  });

  return sanitized;
};

/**
 * Remove sensitive data from state
 */
const sanitizeState = (state) => {
  if (!state || typeof state !== 'object') return state;

  const sanitized = { ...state };
  const sensitiveKeys = ['password', 'token', 'credentials', 'accessKey', 'secretKey'];

  Object.keys(sanitized).forEach((key) => {
    if (sensitiveKeys.some((sk) => key.toLowerCase().includes(sk.toLowerCase()))) {
      sanitized[key] = '***REDACTED***';
    }
  });

  return sanitized;
};

/**
 * Convert object to array format for backend
 */
const objectToArray = (obj) => {
  if (!obj) return null;
  if (Array.isArray(obj)) return obj;
  
  // Convert object to array of values
  return Object.keys(obj).map(key => ({
    key,
    value: obj[key]
  }));
};

/**
 * Format timestamp to match backend validation (Y-m-d\TH:i:s.000\Z)
 */
const formatTimestamp = () => {
  const now = new Date();
  
  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, '0');
  const day = String(now.getUTCDate()).padStart(2, '0');
  const hours = String(now.getUTCHours()).padStart(2, '0');
  const minutes = String(now.getUTCMinutes()).padStart(2, '0');
  const seconds = String(now.getUTCSeconds()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000Z`;
};

/**
 * Send debug log to backend
 */
const sendDebugLog = async (payload) => {
  try {
    const response = await fetch(`${baseUrl}debug/log`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      logger.error('Failed to send debug log:', response.status, response.statusText);
    }
  } catch (error) {
    // Silently fail to avoid disrupting app
    logger.error('Error sending debug log:', error);
  }
};

export default remoteDebugMiddleware;